import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, catchError, map, startWith, filter, finalize, takeUntil } from 'rxjs/operators';
import { FlightSearchInterface, AirportGroup, ItineraryServiceService } from '../../services';
import { FromStatusService  } from '../../services';
@Component({
    selector: 'app-from',
    templateUrl: './from.component.html',
    styleUrls: ['./from.component.css'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FromComponent),
            multi: true
        }],
    standalone: false
})
export class FromComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() options: FlightSearchInterface[] = [];
  @Input() placeholder: string = 'Enter location';
  @Input() isReadOnly: boolean = false;
  /** Element the suggestions panel hangs from. Defaults to the input; the search widget passes its whole field. */
  @Input() panelOrigin?: MatAutocompleteOrigin;
  /** Width of the suggestions panel; wide enough for the grouped airport rows. */
  @Input() panelWidth: string | number = '440px';
  control = new FormControl();
  isLoadingKiwi = false;
  citiesOptions$: Observable<AirportGroup[]>;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private skipNextSearch = false;
  /** The airport behind the current IATA value, so the input can show "City (IATA)". */
  private lastSelected: FlightSearchInterface | null = null;
  private readonly destroy$ = new Subject<void>();

  private recentSearchesSubject = new BehaviorSubject<FlightSearchInterface[]>([]);
  recentSearches$: Observable<FlightSearchInterface[]> = this.recentSearchesSubject.asObservable();
  airports: FlightSearchInterface[];


  constructor(
    private itineraryService: ItineraryServiceService,
    private fromService:FromStatusService,
  ) {
    this.loadRecentSearches();
  }

  ngOnInit() {

    const recentSearches = this.loadRecentSearches();
    this.recentSearchesSubject.next(recentSearches);

    // Selections made elsewhere (swap button, popular routes, results-page
    // prefill) arrive through the shared service; mirror them into the input.
    this.fromService.valueChange.pipe(takeUntil(this.destroy$)).subscribe((airport: any) => {
      if (airport && airport.iata) {
        this.lastSelected = airport;
        this.control.setValue(airport, { emitEvent: false });
      }
    });

    this.citiesOptions$ = this.control.valueChanges.pipe(
      debounceTime(200),  // Wait for 200ms pause in typing
      filter(value => typeof value === 'string'),  // Ensure the value is a string
      map(value => value.trim()),  // Trim the whitespace
      distinctUntilChanged(),  // Only continue if the value has changed
      tap(() => this.isLoadingKiwi = true),  // Set loading to true right before the switchMap
      switchMap(value => {
        if (value.length < 3) {
          // Do not emit any options if under 3 characters
          this.isLoadingKiwi = false;
          this.skipNextSearch = false
          return of([]);
        }
        if (this.skipNextSearch) {
          this.isLoadingKiwi = false;
          return of([]);
        } else {
          return this.search(value).pipe(
            finalize(() => this.isLoadingKiwi = false)  // Ensure loading indicator is turned off after search
          );
        }
      }),
      map(airports => this.itineraryService.groupAirports(airports)),  // Group by city for clearer presentation
      catchError(error => {
        console.error('Error fetching data: ', error);
        this.isLoadingKiwi = false;  // Ensure loading indicator is turned off on error
        return of([]);
      })
    );
    


  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  saveRecentSearch(search: FlightSearchInterface) {
    let recentSearches = this.loadRecentSearches();
  
    // Remove duplicates (based on iata code)
    recentSearches = recentSearches.filter(item => item.iata !== search.iata);
  
    recentSearches = recentSearches.reverse(); 
    // Add new search at the beginning and keep only top 5
    recentSearches = [search, ...recentSearches].slice(0, 5);
  
 
  
    localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    this.recentSearchesSubject.next(recentSearches);
  }

  loadRecentSearches() {
    const storedSearches = localStorage.getItem('recentSearches');
    return storedSearches ? JSON.parse(storedSearches) : [];
  }

  // search(value: string): Observable<FlightSearchInterface[]> {
  //   this.isLoadingKiwi = true; // Show loading indicator
  //   return this.itineraryService.getFlights({'channel': 'flights', 'city_query': value}).pipe(
  //     map(response => this.convertObjectToArray(response)),
  //     catchError(error => {
  //       console.error('Error fetching data: ', error);
  //       this.isLoadingKiwi = false; // Hide loading indicator on error
  //       return of([]);
  //     })
  //   );
  // }

  search(query: string): Observable<FlightSearchInterface[]> {
    return this.itineraryService.searchAirports(query).pipe(
      tap((results) => this.airports = results),
      catchError((error) => {
        console.error('Search error:', error);
        return of([]);  // Return an empty array as a fallback in case of error
      })
    );
  }

  convertObjectToArray(response: any): FlightSearchInterface[] {
    return Object.keys(response).map(key => ({
      ...response[key],
      name: `${response[key].city} - ${response[key].name}`,
      iata: response[key].iata,
      city: response[key].city,
      country: response[key].country
    }));
  }


  writeValue(obj: any): void {
    // Prevent triggering valueChanges when setting value programmatically.
    if (typeof obj === 'string' && this.lastSelected && this.lastSelected.iata === obj) {
      this.control.setValue(this.lastSelected, { emitEvent: false });
      return;
    }
    if (!obj) {
      this.lastSelected = null;
    }
    this.control.setValue(obj, { emitEvent: false });
  }

  /** Render a selected airport as "Nairobi (NBO)"; typed text is shown as-is. */
  displayAirport = (value: FlightSearchInterface | string | null): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.city ? `${value.city} (${value.iata})` : value.iata;
  };

  registerOnChange(fn: any): void {
    // this.onChange = fn;
    console.log("on change register")
  }

  registerOnTouched(fn: any): void {
    // this.onTouched = fn;
    console.log("on touch register")
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }


  onSelection(event: any): void {
    const selectedValue = event.option.value;
    this.lastSelected = selectedValue;
    this.writeValue(selectedValue.iata);
    this.onChange(selectedValue.iata);
    this.fromService.changeSelectedValue(selectedValue)
    this.onTouched();
    this.skipNextSearch = true;
    this.saveRecentSearch(selectedValue); // Set the flag to skip the next search
  }

  clearInput(): void {
    this.control.setValue('');
  }

  onBlur(): void {
    this.control.markAsTouched();
  }

  onFocus(event?: FocusEvent): void {
    this.onChange('')
    this.control.markAsUntouched();
    // Select the current value so typing replaces "Nairobi (NBO)" instead of appending to it.
    const input = event?.target as HTMLInputElement | undefined;
    if (input && typeof input.select === 'function') {
      setTimeout(() => input.select());
    }
  }
  
}
